import { useState, useMemo, useCallback } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { mdiChevronDown, mdiClose, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { usePropertyDefinitionSearch } from '../../hooks/usePropertyDefinitionSearch'
import { usePropertyEnumValues, usePropertyDefinitionsByCodes, type PropertyDefinition } from '../../hooks/useQueries'

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'in', label: 'in' },
  { value: 'intersects', label: 'intersects' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_set', label: 'is set' },
]

const OPERATORS_BY_TYPE: Record<string, string[]> = {
  text: ['eq', 'neq', 'in', 'is_empty', 'is_set'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'is_empty', 'is_set'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_set'],
  boolean: ['eq', 'neq', 'is_empty', 'is_set'],
  enum: ['eq', 'neq', 'in', 'intersects', 'is_empty', 'is_set'],
  json: ['is_empty', 'is_set'],
}

const NO_VALUE_OPS = ['is_empty', 'is_set']
const MULTI_VALUE_OPS = ['in', 'intersects']

interface ConditionBuilderProps {
  conditions: any
  onChange: (conditions: any) => void
  org?: string
  defaultDateMode?: 'absolute' | 'relative'
}

function parseValue(val: string): any {
  if (val === 'true') return true
  if (val === 'false') return false
  if (!isNaN(Number(val)) && val.trim() !== '') return Number(val)
  return val
}

function isLeaf(cond: any): boolean {
  return cond && typeof cond === 'object' && 'property' in cond
}

function isEmpty(cond: any): boolean {
  return !cond || (typeof cond === 'object' && Object.keys(cond).length === 0)
}

function isGroup(cond: any): boolean {
  return cond && typeof cond === 'object' && ('all' in cond || 'any' in cond)
}

function isNot(cond: any): boolean {
  return cond && typeof cond === 'object' && 'not' in cond
}

/** Normalize a condition tree into an editable form: always a group at root */
function normalizeToGroup(cond: any): { combinator: 'all' | 'any'; children: any[] } {
  if (isEmpty(cond)) return { combinator: 'all', children: [] }
  if (isGroup(cond)) {
    const combinator = 'all' in cond ? 'all' : 'any'
    return { combinator: combinator as 'all' | 'any', children: cond[combinator] || [] }
  }
  // Single leaf or not → wrap in all
  return { combinator: 'all', children: [cond] }
}

/** Convert group back to condition tree */
function groupToCondition(combinator: string, children: any[]): any {
  if (children.length === 0) return {}
  if (children.length === 1 && combinator === 'all') return children[0]
  return { [combinator]: children }
}

function getOperatorsForType(dataType?: string): typeof OPERATORS {
  if (!dataType || !(dataType in OPERATORS_BY_TYPE)) return OPERATORS
  const allowed = OPERATORS_BY_TYPE[dataType]
  return OPERATORS.filter(op => allowed.includes(op.value))
}

function findPropertyDef(propertyCode: string, propertyDefinitionsAll: PropertyDefinition[]): PropertyDefinition | undefined {
  return propertyDefinitionsAll.find(pd => pd.code === propertyCode)
}

/** Property selector that works by code, backed by ApiSearchableSelect */
function PropertyCodeSelect({ org, value, onChange, testId, propertyDefinitionsAll }: {
  org: string
  value: string
  onChange: (info: { code: string; dataType?: string; uid?: string }) => void
  testId: string
  propertyDefinitionsAll: PropertyDefinition[]
}) {
  const { searchPropertyDefinitions, initialOptions } = usePropertyDefinitionSearch(org)

  // Build options keyed by code instead of UID
  const codeOptions: ApiSearchableSelectOption[] = useMemo(() =>
    initialOptions.map(opt => ({
      ...opt,
      value: (opt.data?.code as string) || opt.value,
    })),
    [initialOptions]
  )

  // Build allOptions from the full merged property definitions list
  // so ApiSearchableSelect can resolve display labels for codes not in initialOptions
  const allCodeOptions: ApiSearchableSelectOption[] = useMemo(() =>
    propertyDefinitionsAll.map(pd => {
      const name = typeof pd.name === 'object' && pd.name !== null
        ? (pd.name as Record<string, string>).en || (pd.name as Record<string, string>).fr || Object.values(pd.name)[0] || pd.code || pd.uid
        : pd.name || pd.code || pd.uid
      return {
        value: pd.code || '',
        label: `${name} (${pd.data_type})`,
        data: { code: pd.code, data_type: pd.data_type, uid: pd.uid }
      }
    }),
    [propertyDefinitionsAll]
  )

  const selectedCode = value

  const handleSearch = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    const results = await searchPropertyDefinitions(query)
    return results.map(opt => ({
      ...opt,
      value: (opt.data?.code as string) || opt.value,
    }))
  }, [searchPropertyDefinitions])

  const handleSelect = useCallback((option: ApiSearchableSelectOption) => {
    const code = (option.data?.code as string) || option.value
    const dataType = option.data?.data_type as string | undefined
    const uid = option.data?.uid as string | undefined
    onChange({ code, dataType, uid })
  }, [onChange])

  const handleValueChange = useCallback((val: string) => {
    // Find the option to get data_type and uid
    const opt = codeOptions.find(o => o.value === val)
    const dataType = opt?.data?.data_type as string | undefined
    const uid = opt?.data?.uid as string | undefined
    onChange({ code: val, dataType, uid })
  }, [onChange, codeOptions])

  return (
    <ApiSearchableSelect
      value={selectedCode}
      onValueChange={handleValueChange}
      onOptionSelect={handleSelect}
      onSearch={handleSearch}
      placeholder="Select property..."
      searchPlaceholder="Search properties..."
      emptyMessage="No properties found"
      initialOptions={codeOptions}
      allOptions={allCodeOptions}
      data-testid={testId}
    />
  )
}

const RELATIVE_DATE_UNITS = [
  { value: 'd', label: 'days' },
  { value: 'w', label: 'weeks' },
  { value: 'm', label: 'months' },
  { value: 'y', label: 'years' },
]

function isRelativeDate(val: any): boolean {
  return typeof val === 'string' && val.startsWith('$now')
}

function parseRelativeDate(val: string): { offset: number; unit: string } {
  const match = val.match(/^\$now([+-]\d+)([dwmy])$/)
  if (!match) return { offset: 0, unit: 'd' }
  return { offset: parseInt(match[1], 10), unit: match[2] }
}

function buildRelativeDate(offset: number, unit: string): string {
  if (offset === 0) return '$now'
  const sign = offset > 0 ? '+' : ''
  return `$now${sign}${offset}${unit}`
}

function resolveRelativeDate(offset: number, unit: string): string {
  const now = new Date()
  switch (unit) {
    case 'd': now.setDate(now.getDate() + offset); break
    case 'w': now.setDate(now.getDate() + offset * 7); break
    case 'm': now.setMonth(now.getMonth() + offset); break
    case 'y': now.setFullYear(now.getFullYear() + offset); break
  }
  return now.toISOString().slice(0, 10)
}

/** Date value input with absolute/relative toggle */
function DateValueInput({ value, onChange, defaultMode, testIdPrefix }: {
  value: any
  onChange: (val: any) => void
  defaultMode: 'absolute' | 'relative'
  testIdPrefix: string
}) {
  const initialMode = value !== undefined && value !== ''
    ? (isRelativeDate(value) ? 'relative' : 'absolute')
    : defaultMode
  const [mode, setMode] = useState<'absolute' | 'relative'>(initialMode)

  const handleModeChange = (newMode: 'absolute' | 'relative') => {
    setMode(newMode)
    if (newMode === 'relative') {
      onChange('$now-12m')
    } else {
      onChange('')
    }
  }

  if (mode === 'relative') {
    const { offset, unit } = isRelativeDate(value) ? parseRelativeDate(value) : { offset: -12, unit: 'm' }
    const resolvedDate = resolveRelativeDate(offset, unit)
    return (
      <div className="flex-1 flex gap-1 items-center">
        <Button
          variant="outline"
          size="sm"
          className="text-xs shrink-0"
          onClick={() => handleModeChange('absolute')}
          data-testid={`${testIdPrefix}-date-mode`}
        >
          Rel
        </Button>
        <Input
          type="number"
          value={offset}
          onChange={(e) => {
            const n = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
            onChange(buildRelativeDate(isNaN(n) ? 0 : n, unit))
          }}
          className="w-16"
          data-testid={`${testIdPrefix}-rel-offset`}
        />
        <Select value={unit} onValueChange={(u) => onChange(buildRelativeDate(offset, u))}>
          <SelectTrigger className="w-24" data-testid={`${testIdPrefix}-rel-unit`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATIVE_DATE_UNITS.map(u => (
              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground shrink-0">{resolvedDate}</span>
      </div>
    )
  }

  return (
    <div className="flex-1 flex gap-1 items-center">
      <Button
        variant="outline"
        size="sm"
        className="text-xs shrink-0"
        onClick={() => handleModeChange('relative')}
        data-testid={`${testIdPrefix}-date-mode`}
      >
        Abs
      </Button>
      <Input
        type="date"
        value={value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1"
        data-testid={`${testIdPrefix}-value`}
      />
    </div>
  )
}

/** Type-aware value input */
function ConditionValueInput({ dataType, op, value, values, onChange, org, propertyUid, testIdPrefix, defaultDateMode }: {
  dataType?: string
  op: string
  value: any
  values: any[] | undefined
  onChange: (field: 'value' | 'values', val: any) => void
  org?: string
  propertyUid?: string
  testIdPrefix: string
  defaultDateMode?: 'absolute' | 'relative'
}) {
  const isMulti = MULTI_VALUE_OPS.includes(op)
  const { data: enumValues = [] } = usePropertyEnumValues(
    org || '',
    propertyUid || '',
    { enabled: dataType === 'enum' && !!org && !!propertyUid }
  )

  if (NO_VALUE_OPS.includes(op)) {
    return <div className="flex-1" />
  }

  // Boolean: true/false dropdown
  if (dataType === 'boolean') {
    return (
      <Select
        value={value === undefined ? '' : String(value)}
        onValueChange={(v) => onChange('value', v === 'true')}
      >
        <SelectTrigger className="flex-1" data-testid={`${testIdPrefix}-value`}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">true</SelectItem>
          <SelectItem value="false">false</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  // Enum: dropdown of enum values
  if (dataType === 'enum' && enumValues.length > 0) {
    if (isMulti) {
      // Multi-value: checkboxes
      const selectedValues = values || []
      return (
        <div className="flex-1 flex flex-wrap gap-1 items-center min-h-9 border rounded-md px-2 py-1">
          {enumValues.map(ev => {
            const enumName = typeof ev.name === 'object' && ev.name !== null
              ? (ev.name as Record<string, string>).en || (ev.name as Record<string, string>).fr || ev.value
              : ev.value
            const isSelected = selectedValues.includes(ev.value)
            return (
              <Button
                key={ev.uid}
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-xs"
                onClick={() => {
                  const next = isSelected
                    ? selectedValues.filter((v: string) => v !== ev.value)
                    : [...selectedValues, ev.value]
                  onChange('values', next)
                }}
                data-testid={`${testIdPrefix}-enum-${ev.value}`}
              >
                {enumName}
              </Button>
            )
          })}
        </div>
      )
    }

    // Single value: select dropdown
    const enumName = (ev: any) => {
      if (typeof ev.name === 'object' && ev.name !== null) {
        return (ev.name as Record<string, string>).en || (ev.name as Record<string, string>).fr || ev.value
      }
      return ev.value
    }
    return (
      <Select
        value={value === undefined ? '' : String(value)}
        onValueChange={(v) => onChange('value', v)}
      >
        <SelectTrigger className="flex-1" data-testid={`${testIdPrefix}-value`}>
          <SelectValue placeholder="Select value..." />
        </SelectTrigger>
        <SelectContent>
          {enumValues.map(ev => (
            <SelectItem key={ev.uid} value={ev.value}>{enumName(ev)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  // Multi-value ops for non-enum types
  if (isMulti) {
    return (
      <Input
        placeholder="val1, val2"
        value={(values || []).join(', ')}
        onChange={(e) => onChange('values', e.target.value.split(',').map(s => parseValue(s.trim())))}
        className="flex-1"
        data-testid={`${testIdPrefix}-values`}
      />
    )
  }

  // Number input
  if (dataType === 'number') {
    return (
      <Input
        type="number"
        placeholder="value"
        value={value === undefined ? '' : String(value)}
        onChange={(e) => onChange('value', e.target.value === '' ? '' : Number(e.target.value))}
        className="flex-1"
        data-testid={`${testIdPrefix}-value`}
      />
    )
  }

  // Date input with absolute/relative toggle
  if (dataType === 'date') {
    return (
      <DateValueInput
        value={value}
        onChange={(val) => onChange('value', val)}
        defaultMode={defaultDateMode || 'absolute'}
        testIdPrefix={testIdPrefix}
      />
    )
  }

  // Default: text input
  return (
    <Input
      placeholder="value"
      value={value === undefined ? '' : String(value)}
      onChange={(e) => onChange('value', parseValue(e.target.value))}
      className="flex-1"
      data-testid={`${testIdPrefix}-value`}
    />
  )
}

function LeafEditor({ condition, onChange, onRemove, testIdPrefix, org, propertyDefinitionsAll, defaultDateMode }: {
  condition: any
  onChange: (c: any) => void
  onRemove: () => void
  testIdPrefix: string
  org?: string
  propertyDefinitionsAll: PropertyDefinition[]
  defaultDateMode?: 'absolute' | 'relative'
}) {
  const property = condition.property || ''
  const op = condition.op || 'eq'
  const value = condition.value
  const values = condition.values

  const propDef = useMemo(() => findPropertyDef(property, propertyDefinitionsAll), [property, propertyDefinitionsAll])
  const dataType = propDef?.data_type || condition._dataType
  const propertyUid = propDef?.uid || condition._propertyUid
  const availableOps = useMemo(() => getOperatorsForType(dataType), [dataType])

  const update = (field: string, val: any) => {
    const next = { ...condition, [field]: val }
    // Clean up value/values when switching ops
    if (field === 'op') {
      if (NO_VALUE_OPS.includes(val)) {
        delete next.value
        delete next.values
      } else if (MULTI_VALUE_OPS.includes(val)) {
        delete next.value
        if (!next.values) next.values = []
      } else {
        delete next.values
        if (next.value === undefined) next.value = ''
      }
    }
    onChange(next)
  }

  const handlePropertyChange = useCallback((info: { code: string; dataType?: string; uid?: string }) => {
    const next = { ...condition, property: info.code }
    // Store data type and uid in the condition for reliable lookup
    if (info.dataType) next._dataType = info.dataType
    if (info.uid) next._propertyUid = info.uid
    // Reset operator if current op is not valid for new type
    if (info.dataType && info.dataType in OPERATORS_BY_TYPE) {
      const allowed = OPERATORS_BY_TYPE[info.dataType]
      if (!allowed.includes(next.op || 'eq')) {
        next.op = allowed[0]
        // Clean up value/values for new op
        if (NO_VALUE_OPS.includes(next.op)) {
          delete next.value
          delete next.values
        } else if (MULTI_VALUE_OPS.includes(next.op)) {
          delete next.value
          if (!next.values) next.values = []
        } else {
          delete next.values
          if (next.value === undefined) next.value = ''
        }
      }
    }
    // Reset value when changing property
    if (next.value !== undefined) next.value = ''
    if (next.values !== undefined) next.values = []
    onChange(next)
  }, [condition, onChange])

  const handleValueChange = useCallback((field: 'value' | 'values', val: any) => {
    update(field, val)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, onChange])

  return (
    <div className="flex gap-2 items-center">
      {org ? (
        <div className="flex-1">
          <PropertyCodeSelect
            org={org}
            value={property}
            onChange={handlePropertyChange}
            testId={`${testIdPrefix}-property`}
            propertyDefinitionsAll={propertyDefinitionsAll}
          />
        </div>
      ) : (
        <Input
          placeholder="property_code"
          value={property}
          onChange={(e) => update('property', e.target.value)}
          className="flex-1"
          data-testid={`${testIdPrefix}-property`}
        />
      )}
      <Select value={op} onValueChange={(v) => update('op', v)}>
        <SelectTrigger className="w-[100px]" data-testid={`${testIdPrefix}-op`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {availableOps.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ConditionValueInput
        dataType={dataType}
        op={op}
        value={value}
        values={values}
        onChange={handleValueChange}
        org={org}
        propertyUid={propertyUid}
        testIdPrefix={testIdPrefix}
        defaultDateMode={defaultDateMode}
      />
      <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`${testIdPrefix}-remove`}>
        <AppIcon path={mdiClose} size="xs" />
      </Button>
    </div>
  )
}

function ConditionGroup({ combinator, children, onChange, onRemove, depth, testIdPrefix, org, propertyDefinitionsAll, defaultDateMode }: {
  combinator: 'all' | 'any'
  children: any[]
  onChange: (cond: any) => void
  onRemove?: () => void
  depth: number
  testIdPrefix: string
  org?: string
  propertyDefinitionsAll: PropertyDefinition[]
  defaultDateMode?: 'absolute' | 'relative'
}) {
  const updateChild = (index: number, child: any) => {
    const next = [...children]
    next[index] = child
    onChange(groupToCondition(combinator, next))
  }

  const removeChild = (index: number) => {
    const next = children.filter((_, i) => i !== index)
    onChange(groupToCondition(combinator, next))
  }

  const addLeaf = () => {
    onChange(groupToCondition(combinator, [...children, { property: '', op: 'eq', value: '' }]))
  }

  const addGroup = (type: 'all' | 'any') => {
    onChange(groupToCondition(combinator, [...children, { [type]: [{ property: '', op: 'eq', value: '' }] }]))
  }

  const addNot = () => {
    onChange(groupToCondition(combinator, [...children, { not: { property: '', op: 'eq', value: '' } }]))
  }

  const toggleCombinator = () => {
    const next = combinator === 'all' ? 'any' : 'all'
    onChange(groupToCondition(next, children))
  }

  const borderColors = depth === 0
    ? 'border-border'
    : combinator === 'all'
      ? 'border-blue-300 dark:border-blue-700'
      : 'border-orange-300 dark:border-orange-700'

  const bgColors = depth === 0
    ? ''
    : combinator === 'all'
      ? 'bg-blue-50/50 dark:bg-blue-950/20'
      : 'bg-orange-50/50 dark:bg-orange-950/20'

  return (
    <div className={`space-y-2 ${depth > 0 ? `border rounded-md p-3 ${borderColors} ${bgColors}` : ''}`}>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleCombinator}
          className={`text-xs font-semibold px-3 ${
            combinator === 'all'
              ? 'text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-600'
              : 'text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-600'
          }`}
          data-testid={`${testIdPrefix}-combinator`}
        >
          {combinator === 'all' ? 'AND' : 'OR'}
        </Button>
        <span className="text-xs text-muted-foreground">
          {combinator === 'all' ? 'All conditions must match' : 'Any condition must match'}
        </span>
        {onRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove} className="ml-auto" data-testid={`${testIdPrefix}-remove`}>
            <AppIcon path={mdiClose} size="xs" />
          </Button>
        )}
      </div>

      {children.length === 0 && (
        <div className="text-sm text-muted-foreground italic py-1 pl-2">(always matches - no conditions)</div>
      )}

      {children.map((child, index) => {
        const childPrefix = `${testIdPrefix}-${index}`

        if (isNot(child)) {
          return (
            <NotEditor
              key={index}
              condition={child.not}
              onChange={(inner) => updateChild(index, { not: inner })}
              onRemove={() => removeChild(index)}
              depth={depth + 1}
              testIdPrefix={`${childPrefix}-not`}
              org={org}
              propertyDefinitionsAll={propertyDefinitionsAll}
              defaultDateMode={defaultDateMode}
            />
          )
        }

        if (isGroup(child)) {
          const childComb = 'all' in child ? 'all' : 'any'
          return (
            <ConditionGroup
              key={index}
              combinator={childComb as 'all' | 'any'}
              children={child[childComb] || []}
              onChange={(c) => updateChild(index, c)}
              onRemove={() => removeChild(index)}
              depth={depth + 1}
              testIdPrefix={childPrefix}
              org={org}
              propertyDefinitionsAll={propertyDefinitionsAll}
              defaultDateMode={defaultDateMode}
            />
          )
        }

        if (isLeaf(child)) {
          return (
            <LeafEditor
              key={index}
              condition={child}
              onChange={(c) => updateChild(index, c)}
              onRemove={() => removeChild(index)}
              testIdPrefix={childPrefix}
              org={org}
              propertyDefinitionsAll={propertyDefinitionsAll}
              defaultDateMode={defaultDateMode}
            />
          )
        }

        return null
      })}

      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={addLeaf} data-testid={`${testIdPrefix}-add-condition`}>
          <AppIcon path={mdiPlus} size="xs" className="mr-1" />
          Condition
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`${testIdPrefix}-add-group`}>
              <AppIcon path={mdiPlus} size="xs" className="mr-1" />
              Group
              <AppIcon path={mdiChevronDown} size="xs" tone="muted" className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => addGroup('all')} data-testid={`${testIdPrefix}-add-all-group`}>
              AND group
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addGroup('any')} data-testid={`${testIdPrefix}-add-any-group`}>
              OR group
            </DropdownMenuItem>
            <DropdownMenuItem onClick={addNot} data-testid={`${testIdPrefix}-add-not`}>
              NOT
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

function NotEditor({ condition, onChange, onRemove, depth, testIdPrefix, org, propertyDefinitionsAll, defaultDateMode }: {
  condition: any
  onChange: (inner: any) => void
  onRemove: () => void
  depth: number
  testIdPrefix: string
  org?: string
  propertyDefinitionsAll: PropertyDefinition[]
  defaultDateMode?: 'absolute' | 'relative'
}) {
  return (
    <div className="border rounded-md p-3 border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/20 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-red-700 dark:text-red-400 border border-red-300 dark:border-red-600 rounded px-2 py-0.5">NOT</span>
        <span className="text-xs text-muted-foreground">Negates the condition below</span>
        <Button variant="ghost" size="sm" onClick={onRemove} className="ml-auto" data-testid={`${testIdPrefix}-remove`}>
          <AppIcon path={mdiClose} size="xs" />
        </Button>
      </div>
      {isGroup(condition) ? (
        <ConditionGroup
          combinator={'all' in condition ? 'all' : 'any'}
          children={condition['all' in condition ? 'all' : 'any'] || []}
          onChange={onChange}
          depth={depth + 1}
          testIdPrefix={`${testIdPrefix}-inner`}
          org={org}
          propertyDefinitionsAll={propertyDefinitionsAll}
          defaultDateMode={defaultDateMode}
        />
      ) : isLeaf(condition) ? (
        <LeafEditor
          condition={condition}
          onChange={onChange}
          onRemove={onRemove}
          testIdPrefix={`${testIdPrefix}-leaf`}
          org={org}
          propertyDefinitionsAll={propertyDefinitionsAll}
          defaultDateMode={defaultDateMode}
        />
      ) : (
        <div className="text-sm text-muted-foreground italic">(empty)</div>
      )}
    </div>
  )
}

/** Strip internal _dataType/_propertyUid fields from condition tree before saving */
export function stripInternalFields(cond: any): any {
  if (!cond || typeof cond !== 'object') return cond
  if (Array.isArray(cond)) return cond.map(stripInternalFields)
  const cleaned: any = {}
  for (const [key, value] of Object.entries(cond)) {
    if (key === '_dataType' || key === '_propertyUid') continue
    if (key === 'all' || key === 'any') {
      cleaned[key] = (value as any[]).map(stripInternalFields)
    } else if (key === 'not') {
      cleaned[key] = stripInternalFields(value)
    } else {
      cleaned[key] = value
    }
  }
  return cleaned
}

/** Extract all property codes from a condition tree */
function extractPropertyCodes(cond: any): string[] {
  if (!cond || typeof cond !== 'object') return []
  if (isLeaf(cond)) return cond.property ? [cond.property] : []
  if (isNot(cond)) return extractPropertyCodes(cond.not)
  if (isGroup(cond)) {
    const children = cond.all || cond.any || []
    return children.flatMap(extractPropertyCodes)
  }
  return []
}

export function ConditionBuilder({ conditions, onChange, org, defaultDateMode }: ConditionBuilderProps) {
  const { propertyDefinitionsAll } = usePropertyDefinitionSearch(org || '')
  const { combinator, children } = normalizeToGroup(conditions)

  // Find property codes used in conditions that aren't in the default list
  const missingCodes = useMemo(() => {
    const usedCodes = extractPropertyCodes(conditions)
    const knownCodes = new Set(propertyDefinitionsAll.map(pd => pd.code))
    return [...new Set(usedCodes.filter(c => c && !knownCodes.has(c)))]
  }, [conditions, propertyDefinitionsAll])

  const { data: missingDefs = [] } = usePropertyDefinitionsByCodes(org || '', missingCodes.length > 0 ? missingCodes : undefined)

  // Merge default + fetched-by-code definitions, deduplicating by code
  const mergedPropertyDefs = useMemo(() => {
    if (missingDefs.length === 0) return propertyDefinitionsAll
    const knownCodes = new Set(propertyDefinitionsAll.map(pd => pd.code))
    const extras = missingDefs.filter(pd => !knownCodes.has(pd.code))
    return [...propertyDefinitionsAll, ...extras]
  }, [propertyDefinitionsAll, missingDefs])

  return (
    <ConditionGroup
      combinator={combinator}
      children={children}
      onChange={onChange}
      depth={0}
      testIdPrefix="cond"
      org={org}
      propertyDefinitionsAll={mergedPropertyDefs}
      defaultDateMode={defaultDateMode}
    />
  )
}
